import { useEffect, useState } from "react";
import {
  loadCaseMetricConfiguration,
  updateCaseMetricConfiguration,
  type CaseMetricConfiguration as Configuration,
  type CustomCaseMetric,
} from "./api";

export function CaseMetricConfiguration({
  workspaceName,
}: {
  workspaceName?: string;
}) {
  const [saved, setSaved] = useState<Configuration | null>(null);
  const [items, setItems] = useState<CustomCaseMetric[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [reload, setReload] = useState(0);
  const [newMetricKey, setNewMetricKey] = useState<string | null>(null);
  useEffect(() => {
    const controller = new AbortController();
    setSaved(null);
    setError("");
    setNotice("");
    void loadCaseMetricConfiguration(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) {
          setSaved(data);
          setItems(data.items);
        }
      })
      .catch((cause) => {
        if (!controller.signal.aborted)
          setError(cause instanceof Error ? cause.message : String(cause));
      });
    return () => controller.abort();
  }, [reload]);
  function change(next: CustomCaseMetric[]) {
    setItems(next);
    setNotice("");
  }
  function move(index: number, offset: number) {
    const next = [...items];
    [next[index], next[index + offset]] = [next[index + offset], next[index]];
    change(next);
  }
  async function save() {
    if (!saved || saving) return;
    setSaving(true);
    setError("");
    setNotice("");
    try {
      const data = await updateCaseMetricConfiguration(saved.version, items);
      setSaved(data);
      setItems(data.items);
      setNotice("案件指标配置已保存");
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : String(cause));
    } finally {
      setSaving(false);
    }
  }
  const dirty = saved && JSON.stringify(items) !== JSON.stringify(saved.items);
  return (
    <div className="basic-configuration-content" role="tabpanel">
      <div className="basic-configuration-section-heading">
        <h2>自定义案件指标</h2>
        <p>
          当前工作空间：{workspaceName || "当前空间"}
          。指标按此处顺序展示；禁用后隐藏已有标记，保留历史记录，重新启用可恢复。
        </p>
      </div>
      {error && (
        <p role="alert" className="basic-configuration-error">
          {error}
        </p>
      )}
      {!saved ? (
        <p>正在加载指标配置…</p>
      ) : (
        <>
          <h3>系统自动指标</h3>
          <div className="automatic-metric-list">
            {saved.automatic.map((item) => (
              <span key={item.key}>
                {item.name}
                <small>自动 · 不可管理</small>
              </span>
            ))}
          </div>
          <div className="metric-config-heading">
            <h3>自定义指标</h3>
            <button
              type="button"
              className="basic-configuration-primary"
              disabled={saving || items.length >= 100}
              onClick={() => {
                const key = `CUSTOM_${crypto.randomUUID()}`;
                setNewMetricKey(key);
                change([
                  ...items,
                  { key, name: "", shortName: "", enabled: true },
                ]);
              }}
            >
              新增指标
            </button>
          </div>
          <p className="basic-configuration-status">
            点击“新增指标”填写名称和简写名称（1–2
            个字），再点击“保存指标配置”生效。简写用于案件顶部进度圆点。
          </p>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void save();
            }}
          >
            <fieldset disabled={saving} className="metric-config-fieldset">
              <div className="metric-config-table-wrap">
                <table className="metric-config-table">
                  <thead>
                    <tr>
                      <th>顺序</th>
                      <th>指标名称</th>
                      <th>简写名称</th>
                      <th>状态</th>
                      <th>排序</th>
                    </tr>
                  </thead>
                  <tbody>
                    {items.map((item, index) => (
                      <tr key={item.key}>
                        <td>{index + 1}</td>
                        <td>
                          <input
                            aria-label={`指标名称 ${index + 1}`}
                            autoFocus={item.key === newMetricKey}
                            placeholder="请输入指标名称"
                            required
                            maxLength={40}
                            value={item.name}
                            onChange={(event) =>
                              change(
                                items.map((row) =>
                                  row.key === item.key
                                    ? { ...row, name: event.target.value }
                                    : row,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <input
                            className="metric-short-name"
                            aria-label={`简写名称 ${index + 1}`}
                            required
                            maxLength={2}
                            placeholder="例如：付"
                            value={item.shortName ?? ""}
                            onChange={(event) =>
                              change(
                                items.map((row) =>
                                  row.key === item.key
                                    ? { ...row, shortName: event.target.value }
                                    : row,
                                ),
                              )
                            }
                          />
                        </td>
                        <td>
                          <label>
                            <input
                              type="checkbox"
                              checked={item.enabled}
                              onChange={(event) =>
                                change(
                                  items.map((row) =>
                                    row.key === item.key
                                      ? {
                                          ...row,
                                          enabled: event.target.checked,
                                        }
                                      : row,
                                  ),
                                )
                              }
                            />
                            {item.enabled ? "已启用" : "已禁用"}
                          </label>
                        </td>
                        <td>
                          <div className="metric-sort-actions">
                            <button
                              type="button"
                              disabled={index === 0}
                              aria-label={`上移 ${item.name}`}
                              onClick={() => move(index, -1)}
                            >
                              上移
                            </button>
                            <button
                              type="button"
                              disabled={index === items.length - 1}
                              aria-label={`下移 ${item.name}`}
                              onClick={() => move(index, 1)}
                            >
                              下移
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="basic-configuration-actions">
                <button
                  type="submit"
                  className="basic-configuration-primary"
                  disabled={!dirty}
                >
                  {saving ? "正在保存…" : "保存指标配置"}
                </button>
              </div>
            </fieldset>
          </form>
          <p className="basic-configuration-status">
            自定义指标不能删除。案件进入销售跟进后，可独立勾选各项指标。
          </p>
        </>
      )}
      <button
        type="button"
        disabled={saving}
        onClick={() => {
          if (
            !dirty ||
            window.confirm("重新加载将放弃未保存的修改，是否继续？")
          )
            setReload((value) => value + 1);
        }}
      >
        重新加载
      </button>
      {notice && (
        <p role="status" className="basic-configuration-notice">
          {notice}
        </p>
      )}
    </div>
  );
}
